// SGP Session question interface
export interface SGPSessionQuestion {
  id: number;
  section: string;
  question: string;
  followup?: string[];
  options?: string[];
}

// SGP Session prework interface
export interface SGPSessionPrework {
  id: number;
  question: string;
  followup?: string[];
  attribute?: string;
  optionsConstant?: string;
  exactCount?: number;
  fileAttribute?: string;
  fileAcceptedTypes?: string[];
  markCompleted?: boolean;
}

// SGP Session data type
export interface SGPSessionData {
  index: number;
  sessionNumber: number;
  title: string;
  description: string;
  duration: number;
  questions?: SGPSessionQuestion[];
  prework?: SGPSessionPrework[];
}

// Valid session format types
export type SessionFormat = '1' | '2' | '5';

// SGP Sessions organized by format (1, 2, or 5 sessions)
export const SGP_SESSIONS: Record<SessionFormat, SGPSessionData[]> = {
  '1': [
    {
      index: 1,
      sessionNumber: 1,
      title: 'Comprehensive Growth Strategy Workshop',
      description:
        "An intensive full-day session covering all aspects of your growth strategy. You'll profile your customers, analyze your strategic position, dissect your value chain, identify growth opportunities, and create an actionable plan to scale your business to $100M in revenue.",
      duration: 8,
      questions: [
        {
          id: 1,
          section: 'Reviewing the Current State',
          question:
            'To ensure your team has a common understanding of your current market focus, what are the top five industries your company targets?',
          followup: ['Are the final five surprising?'],
        },
        {
          id: 2,
          section: 'Reviewing the Current State',
          question:
            "After reviewing your company's revenue analysis, are there any surprising or unexpected learnings?",
        },
        {
          id: 3,
          section: 'Reviewing the Current State',
          question:
            "Does the description of your company's products and services align with how your customer would articulate what your company provides?",
          followup: [
            'If not, how would your customers define your products and services?',
          ],
        },
        {
          id: 4,
          section: 'Reviewing the Current State',
          question:
            'To understand the main driver of your revenue, what do your customers like most about your products and services?',
        },
        {
          id: 5,
          section: 'Profiling Your Customer',
          question:
            'To create a detailed understanding of who your customers are, how would you define the demographic profile of the decision maker for your customer?',
        },
        {
          id: 6,
          section: 'Profiling Your Customer',
          question:
            'To understand how your customers think, including their values, attitudes, and lifestyle, how would you define the psychographic profile of the decision maker for your customer?',
        },
        {
          id: 7,
          section: 'Profiling Your Customer',
          question:
            'To analyze how your customers act, including their purchasing behavior and interaction with your products, how would you define the behavioral profile of the typical decision maker for your customer?',
        },
        {
          id: 8,
          section: 'Profiling Your Customer',
          question:
            'To map out where your customers are located and understand their work environments, what is the geographic profile of the typical decision maker for your customer?',
        },
        {
          id: 9,
          section: 'Profiling Your Customer',
          question:
            'If the decision maker is different from the user, what is the profile of the target user of your product or service?',
          followup: ['In their job, what do they care about most?'],
        },
        {
          id: 10,
          section: 'Profiling Your Customer',
          question:
            'To clearly define why your customer comes to you, what are the top three problems that you solve for your customers?',
        },
        {
          id: 11,
          section: 'Profiling Your Customer',
          question:
            'To identify unmet needs and potential new opportunities for product development or service enhancement, what have your customers requested from you that fall outside of your current offerings?',
        },
        {
          id: 12,
          section: 'Sales Approach and Scaleup Support',
          question:
            "What are your organization's strengths and areas of improvement in the enterprise sales function?",
        },
        {
          id: 13,
          section: 'Sales Approach and Scaleup Support',
          question:
            'What technology (ies) (e.g. tools and software) does your company use to aid in the sales process, both internally and externally?',
        },
        {
          id: 14,
          section: 'Sales Approach and Scaleup Support',
          question:
            "Excluding those with whom you already do business, which of Scaleup's current enterprise customers fit your target customer persona?",
        },
        {
          id: 15,
          section: 'Sales Approach and Scaleup Support',
          question:
            'If Scaleup could support your sales process in pursuit of three Fortune 500 corporations, which three would you choose?',
        },
        {
          id: 16,
          section: 'Sales Approach and Scaleup Support',
          question: 'How could Scaleup be most helpful to you in the sales process?',
        },
        {
          id: 17,
          section: 'Sales Approach and Scaleup Support',
          question: 'What do you not want Scaleup to do in this process?',
        },
        {
          id: 18,
          section: 'Projecting Future State',
          question:
            'How should the description of your products and services change over the next seven years (by 2031)?',
        },
        {
          id: 19,
          section: 'Projecting Future State',
          question:
            'How should the top five industries your company targets change in the next seven years (by 2031)?',
        },
        {
          id: 20,
          section: 'Projecting Future State',
          question:
            'How should the revenue analysis change in the next seven years (by 2031)?',
        },
        {
          id: 21,
          section: 'Identifying the Competitors',
          question: "Who are your company's top three to five direct competitors?",
        },
        {
          id: 22,
          section: 'Identifying the Competitors',
          question:
            "If the types of products and services (provided by your company and its direct competitors) didn't exist, what alternatives or substitutes do your customers have to get their needs met?",
        },
        {
          id: 23,
          section: 'Understanding Competitive Positioning',
          question:
            'How does your company differentiate its products and services from the competition?',
        },
        {
          id: 24,
          section: 'Understanding Competitive Positioning',
          question:
            'If your customer were asked, "Why do you choose your company\'s products and/or services?", how would they respond?',
        },
        {
          id: 25,
          section: 'Understanding Competitive Positioning',
          question:
            'If your customer were asked, "Why do you choose {question 6 alternative and substitute products and/or services ?", how would they respond?',
        },
        {
          id: 26,
          section: 'Replicating Great Customer Experiences',
          question:
            'Thinking about customer experience (even in B2B), how do your customers feel when they use your product or service?',
          followup: [
            'e.g., empowered, accomplished, heard, discouraged, confused, upset, happy, etc.',
          ],
        },
        {
          id: 27,
          section: 'Replicating Great Customer Experiences',
          question:
            'Describe three of your best customer success stories, including background, action, and result.',
        },
        {
          id: 28,
          section: 'Replicating Great Customer Experiences',
          question:
            'Which of those success stories, if any, do you believe you should try to replicate and why?',
        },
        {
          id: 29,
          section: 'Identifying Value Chain Activities',
          question:
            'What are your core activities to deliver value to your customers?',
          options: [
            'Develop and Manage Products and Services',
            'Market and Sell Products and Services',
            'Manage Supply Chain for Physical Products',
            'Deliver Services',
            'Manage Customer Service',
            'Acquire, Construct, and Manage Assets',
            'Other (please specify)',
          ],
        },
        {
          id: 30,
          section: 'Identifying Value Chain Activities',
          question:
            'What are your current support activities to deliver value to your customers?',
          options: [
            'Develop Vision and Strategy',
            'Develop and Manage Human Capital',
            'Manage Information Technology (IT)',
            'Manage Financial Resources',
            'Manage Enterprise Risk, Compliance, Remediation, and Resiliency',
            'Manage External Relationships',
            'Develop and Manage Business Capabilities',
            'Other (please specify)',
          ],
        },
        {
          id: 31,
          section: 'Improving Value Chain Activities to Better Serve Customers',
          question:
            'For which value chain activities do you think your company has the highest potential to leverage more technology to deliver better products and services?',
          followup: ['Choose up to 3 activities.'],
        },
        {
          id: 32,
          section: 'Improving Value Chain Activities to Better Serve Customers',
          question: 'Please share the thinking on the previous response.',
        },
        {
          id: 33,
          section: 'Improving Value Chain Activities for Financial Improvement',
          question:
            "After reviewing your company's operating expense analysis, are there any surprising or unexpected learnings?",
        },
        {
          id: 34,
          section: 'Improving Value Chain Activities for Financial Improvement',
          question:
            "After reviewing your company's capital expense analysis, are there any surprising or unexpected learnings?",
        },
        {
          id: 35,
          section: 'Improving Value Chain Activities for Financial Improvement',
          question:
            'For which value chain activities do you think your company has the highest potential to leverage more technology to realize a financial benefit?',
          followup: ['Choose up to 3 activities.'],
        },
        {
          id: 36,
          section: 'Improving Value Chain Activities for Financial Improvement',
          question: 'Please share the thinking on the previous response.',
        },
        {
          id: 37,
          section: 'Improving Value Chain Activities for Financial Improvement',
          question:
            'What are immediate opportunities, if any, to spend more efficiently?',
        },
        {
          id: 38,
          section: 'Improving Value Chain Activities for Competitive Differentiation',
          question:
            'Which activity is the best candidate for increasing product and/or service differentiation from the company\'s competition?',
        },
        {
          id: 39,
          section: 'Improving Value Chain Activities for Competitive Differentiation',
          question: 'Please share the thinking on the previous response.',
        },
        {
          id: 40,
          section: 'Improving Value Chain Activities for Competitive Differentiation',
          question: 'What would be required to make the improvement?',
        },
        {
          id: 41,
          section: 'Industry Revenue Opportunities and Threats',
          question:
            "Using information from the Trends and Opportunities report and from your team's own research, identify three trends leading to significant revenue growth within your industry?",
        },
        {
          id: 42,
          section: 'Industry Revenue Opportunities and Threats',
          question:
            "What implications do these trends have for your company's opportunities to capture additional value?",
        },
        {
          id: 43,
          section: 'Industry Revenue Opportunities and Threats',
          question:
            "What are three trends significantly threatening/impeding revenue growth within your company's industry?",
        },
        {
          id: 44,
          section: 'Industry Revenue Opportunities and Threats',
          question:
            "What implications do these trends have for your company's opportunities to capture additional value?",
        },
        {
          id: 45,
          section: 'New Customers, New Markets',
          question:
            "Based on your company's understanding, what problems could your company solve for new customers that it is already solving for current customers?",
        },
        {
          id: 46,
          section: 'New Customers, New Markets',
          question:
            'What is the price range (per unit of products or services) of the product or service that your company would provide to a new customer over one year?',
        },
        {
          id: 47,
          section: 'New Customers, New Markets',
          question:
            'What is the quantity range of products or services that you would project to provide to a new customer in one year?',
        },
        {
          id: 48,
          section: 'New Customers, New Markets',
          question:
            'How frequently would the new customer buy this quantity of products or services from your company?',
        },
        {
          id: 49,
          section: 'New Customers, New Markets',
          question:
            'How many new customers could you add at that quantity level right now?',
        },
        {
          id: 50,
          section: 'New Customers, New Markets',
          question:
            'Using the responses to questions, 6-9, what is the estimated value of the opportunity (in dollars) to serve these new customers?',
        },
        {
          id: 51,
          section: 'New Customers, New Markets',
          question: 'How could your company reach these new customers?',
        },
        {
          id: 52,
          section: 'New Customers, New Markets',
          question:
            'How will your product or service offerings need to change, if at all, to serve the new customers?',
        },
        {
          id: 53,
          section: 'New Customers, New Markets',
          question:
            'What opportunities does your company have for premium pricing of these products or services?',
        },
        {
          id: 54,
          section: 'New Customers, New Markets',
          question:
            'How would OpEx and/or CapEx spending need to change, if at all, to capture the identified opportunity with the new customers?',
        },
        {
          id: 55,
          section: 'New Products and Services',
          question:
            'Based on your understanding of today, what is one new problem your company could solve for current and new customers (using an existing or new product or service)?',
        },
        {
          id: 56,
          section: 'New Products and Services',
          question:
            'What would be required to solve this new problem (e.g., developing new products and services, forming strategic partnerships to provide new solutions, etc.)?',
        },
        {
          id: 57,
          section: 'New Products and Services',
          question:
            'Please estimate the price range (per unit of the product or service) that your company would charge for the product or service to solve this problem for a customer over one year?',
        },
        {
          id: 58,
          section: 'New Products and Services',
          question:
            'What is the quantity range of the product or service that you would project to provide the customer in one year?',
        },
        {
          id: 59,
          section: 'New Products and Services',
          question:
            'How frequently would a customer buy this quantity of products or services from your company?',
        },
        {
          id: 60,
          section: 'New Products and Services',
          question:
            'For how many customers could you provide this new product or service right now?',
        },
        {
          id: 61,
          section: 'New Products and Services',
          question:
            'Using the responses to questions 17-20, what is the estimated value of the opportunity (in dollars) to solve this new problem for customers?',
        },
        {
          id: 62,
          section: 'New Products and Services',
          question:
            'Revenue Model: For the new product or service, which revenue model(s) would allow you to capture the identified opportunities and work best for your customers?',
          options: [
            'Subscription',
            'Markup/Cost plus',
            'Licensing',
            'Advertising',
            'Affiliate Commission',
            'Data Sales',
            'Project-Based Services',
            'Retainer-Based Services',
            'Tickets, Events, Workshops',
            'Royalties',
            'Rent/Lease',
          ],
        },
        {
          id: 63,
          section: 'New Products and Services',
          question:
            'How would OpEx and/or CapEx spending need to change, if at all, to capture the opportunity of providing the new solution to customers?',
        },
        {
          id: 64,
          section: 'The Big Idea',
          question:
            'Based on your understanding of today and your visualization of tomorrow (from all of the work in the SGP modules and outside of Scaleup), articulate a big idea that will get your company from where it is today to $100M in annual revenue.',
        },
        {
          id: 65,
          section: 'Engaging the Customer',
          question:
            'To capture the value identified in the previous section, how does your company need to change its perception in the marketplace, if at all?',
        },
        {
          id: 66,
          section: 'Engaging the Customer',
          question:
            'To capture the identified value, what experience do customers need to feel when they use your product or service?',
        },
        {
          id: 67,
          section: 'Engaging the Customer',
          question:
            'What experience(s) do you want to avoid giving customers when they use your product or service?',
        },
        {
          id: 68,
          section: 'Go to Market: Getting to the Customer',
          question:
            'Name specific new industries you plan for your company to penetrate.',
        },
        {
          id: 69,
          section: 'Go to Market: Getting to the Customer',
          question: 'Name specific new customers you plan for your company to target.',
        },
        {
          id: 70,
          section: 'Scaling the Infrastructure',
          question:
            'Organization: What areas of functional and/or industry expertise does your company need to build to capture the identified value?',
        },
        {
          id: 71,
          section: 'Scaling the Infrastructure',
          question:
            'Organization: What challenges do you anticipate in growing your team and talent?',
        },
        {
          id: 72,
          section: 'Scaling the Infrastructure',
          question:
            'What types of technology would be most helpful to your company as it scales (i.e. CRM, ERP, financial/accounting, etc.)?',
        },
        {
          id: 73,
          section: 'Scaling the Infrastructure',
          question:
            'What kinds of business support (i.e. recruiting, marketing, legal, technology, etc.) do you think your company will need as it scales?',
        },
        {
          id: 74,
          section: 'Financing the Growth',
          question:
            "Review the company's last three years financial statements (income statement, balance sheet, and statement of cash flows). What tells you that the company is in a good (or not so good) position to embark on the Big Idea?",
        },
        {
          id: 75,
          section: 'Financing the Growth',
          question:
            "Reflecting on the company's financial position and what's needed to deliver on the Big Idea, develop a list of very high level estimates of the major financial impacts that will be experienced as the company delivers on the Big Idea.",
        },
        {
          id: 76,
          section: 'Financing the Growth',
          question:
            'How much capital does the company need to take the desired action steps over the next 12 months?',
        },
        {
          id: 77,
          section: 'Financing the Growth',
          question:
            'Does the company have the capital it needs to take the desired action steps within the next 12 months?',
        },
        {
          id: 78,
          section: 'Financing the Growth',
          question:
            'If the company will need capital over the next twelve months, what types of capital will be considered?',
        },
        {
          id: 79,
          section: 'Financing the Growth',
          question:
            'If the company will need additional capital to reach the $100M+ revenue goal, what types of capital will be considered?',
          options: [
            'Bank term loan',
            'Convertible debt',
            'Factoring',
            'Friends and family',
            'Line of credit',
            'Other loans',
            'Private credit',
            'Private equity',
            'Venture capital',
          ],
        },
      ],
      prework: [
        {
          id: 1,
          question:
            'During the onboarding process, you provided the top five industries your company targets. If there are any edits or updates you would like to make, please do so here.',
          attribute: 'top5Industries',
          optionsConstant: 'industriesServed',
          exactCount: 5,
        },
        {
          id: 2,
          question:
            "Revenue analysis: Upload a spreadsheet segmenting your last year's revenue by product and service, including percentage of total revenue.",
          followup: [
            '(If you prefer, the analyses for 2, 3, and 4 can be separate tabs in the same workbook. In that case, load the file as a response to 2, and mark the other questions as completed.)',
          ],
          fileAttribute: 'revenueAnalysisByProductService',
          fileAcceptedTypes: ['.xlsx', '.xls', '.csv'],
        },
        {
          id: 3,
          question:
            "Revenue analysis: Upload a spreadsheet segmenting your last year's revenue revenue by industry vertical, including percentage of total revenue.",
          fileAttribute: 'revenueAnalysisByCustomerIndustry',
          markCompleted: true,
          fileAcceptedTypes: ['.xlsx', '.xls', '.csv'],
        },
        {
          id: 4,
          question:
            "Revenue analysis: Upload a spreadsheet segmenting your last year's revenue by customer, including percentage of total revenue.",
          fileAttribute: 'revenueAnalysisByCustomer',
          markCompleted: true,
          fileAcceptedTypes: ['.xlsx', '.xls', '.csv'],
        },
        {
          id: 5,
          question:
            "During the onboarding process, you provided a description of your company's products and services. If there are any edits or updates you would like to make, please do so here.",
          attribute: 'description',
        },
        {
          id: 6,
          question:
            'Expense analysis: Upload a spreadsheet listing your key operating expenses (OpEx) from last year in dollar amounts, including percentage of total OpEx.',
          fileAttribute: 'operatingExpenseAnalysis',
          fileAcceptedTypes: ['.xlsx', '.xls', '.csv'],
        },
        {
          id: 7,
          question:
            'Expense analysis: Upload a spreadsheet listing your key capital expenses (CapEx) from last year in dollar amounts, including percentage of total CapEx.',
          fileAttribute: 'capitalExpenseAnalysis',
          fileAcceptedTypes: ['.xlsx', '.xls', '.csv'],
        },
        {
          id: 8,
          question:
            "Financial Statement analysis: Upload a file including the company's last three years financial statements (income statement, balance sheet, and statement of cash flows). The best files are typically PDF files, Microsoft Office documents (Excel, Word, or Powerpoint), or Google Documents (Sheets, Docs, or Slides).",
          fileAttribute: 'financialStatements',
          fileAcceptedTypes: [
            '.pdf',
            '.doc',
            '.docx',
            '.xls',
            '.xlsx',
            '.ppt',
            '.pptx',
          ],
        },
      ],
    },
  ],
  '2': [
    {
      index: 1,
      sessionNumber: 1,
      title: 'Customer and Market Analysis',
      description:
        "In this comprehensive session, you'll profile your customers, analyze your strategic position in the market, and dissect your value chain. You'll gain a deep understanding of your business, customers, competitors, and the activities that drive value.",
      duration: 4,
      questions: [
        {
          id: 1,
          section: 'Reviewing the Current State',
          question:
            'To ensure your team has a common understanding of your current market focus, what are the top five industries your company targets?',
          followup: ['Are the final five surprising?'],
        },
        {
          id: 2,
          section: 'Reviewing the Current State',
          question:
            "After reviewing your company's revenue analysis, are there any surprising or unexpected learnings?",
        },
        {
          id: 3,
          section: 'Reviewing the Current State',
          question:
            "Does the description of your company's products and services align with how your customer would articulate what your company provides?",
          followup: [
            'If not, how would your customers define your products and services?',
          ],
        },
        {
          id: 4,
          section: 'Reviewing the Current State',
          question:
            'To understand the main driver of your revenue, what do your customers like most about your products and services?',
        },
        {
          id: 5,
          section: 'Profiling Your Customer',
          question:
            'To create a detailed understanding of who your customers are, how would you define the demographic profile of the decision maker for your customer?',
        },
        {
          id: 6,
          section: 'Profiling Your Customer',
          question:
            'To understand how your customers think, including their values, attitudes, and lifestyle, how would you define the psychographic profile of the decision maker for your customer?',
        },
        {
          id: 7,
          section: 'Profiling Your Customer',
          question:
            'To analyze how your customers act, including their purchasing behavior and interaction with your products, how would you define the behavioral profile of the typical decision maker for your customer?',
        },
        {
          id: 8,
          section: 'Profiling Your Customer',
          question:
            'To map out where your customers are located and understand their work environments, what is the geographic profile of the typical decision maker for your customer?',
        },
        {
          id: 9,
          section: 'Profiling Your Customer',
          question:
            'If the decision maker is different from the user, what is the profile of the target user of your product or service?',
          followup: ['In their job, what do they care about most?'],
        },
        {
          id: 10,
          section: 'Profiling Your Customer',
          question:
            'To clearly define why your customer comes to you, what are the top three problems that you solve for your customers?',
        },
        {
          id: 11,
          section: 'Profiling Your Customer',
          question:
            'To identify unmet needs and potential new opportunities for product development or service enhancement, what have your customers requested from you that fall outside of your current offerings?',
        },
        {
          id: 12,
          section: 'Sales Approach and Scaleup Support',
          question:
            "What are your organization's strengths and areas of improvement in the enterprise sales function?",
        },
        {
          id: 13,
          section: 'Sales Approach and Scaleup Support',
          question:
            'What technology (ies) (e.g. tools and software) does your company use to aid in the sales process, both internally and externally?',
        },
        {
          id: 14,
          section: 'Sales Approach and Scaleup Support',
          question:
            "Excluding those with whom you already do business, which of Scaleup's current enterprise customers fit your target customer persona?",
        },
        {
          id: 15,
          section: 'Sales Approach and Scaleup Support',
          question:
            'If Scaleup could support your sales process in pursuit of three Fortune 500 corporations, which three would you choose?',
        },
        {
          id: 16,
          section: 'Sales Approach and Scaleup Support',
          question: 'How could Scaleup be most helpful to you in the sales process?',
        },
        {
          id: 17,
          section: 'Sales Approach and Scaleup Support',
          question: 'What do you not want Scaleup to do in this process?',
        },
        {
          id: 18,
          section: 'Projecting Future State',
          question:
            'How should the description of your products and services change over the next seven years (by 2031)?',
        },
        {
          id: 19,
          section: 'Projecting Future State',
          question:
            'How should the top five industries your company targets change in the next seven years (by 2031)?',
        },
        {
          id: 20,
          section: 'Projecting Future State',
          question:
            'How should the revenue analysis change in the next seven years (by 2031)?',
        },
        {
          id: 21,
          section: 'Identifying the Competitors',
          question: "Who are your company's top three to five direct competitors?",
        },
        {
          id: 22,
          section: 'Identifying the Competitors',
          question:
            "If the types of products and services (provided by your company and its direct competitors) didn't exist, what alternatives or substitutes do your customers have to get their needs met?",
        },
        {
          id: 23,
          section: 'Understanding Competitive Positioning',
          question:
            'How does your company differentiate its products and services from the competition?',
        },
        {
          id: 24,
          section: 'Understanding Competitive Positioning',
          question:
            'If your customer were asked, "Why do you choose your company\'s products and/or services?", how would they respond?',
        },
        {
          id: 25,
          section: 'Understanding Competitive Positioning',
          question:
            'If your customer were asked, "Why do you choose {question 6 alternative and substitute products and/or services ?", how would they respond?',
        },
        {
          id: 26,
          section: 'Replicating Great Customer Experiences',
          question:
            'Thinking about customer experience (even in B2B), how do your customers feel when they use your product or service?',
          followup: [
            'e.g., empowered, accomplished, heard, discouraged, confused, upset, happy, etc.',
          ],
        },
        {
          id: 27,
          section: 'Replicating Great Customer Experiences',
          question:
            'Describe three of your best customer success stories, including background, action, and result.',
        },
        {
          id: 28,
          section: 'Replicating Great Customer Experiences',
          question:
            'Which of those success stories, if any, do you believe you should try to replicate and why?',
        },
        {
          id: 29,
          section: 'Identifying Value Chain Activities',
          question:
            'What are your core activities to deliver value to your customers?',
          options: [
            'Develop and Manage Products and Services',
            'Market and Sell Products and Services',
            'Manage Supply Chain for Physical Products',
            'Deliver Services',
            'Manage Customer Service',
            'Acquire, Construct, and Manage Assets',
            'Other (please specify)',
          ],
        },
        {
          id: 30,
          section: 'Identifying Value Chain Activities',
          question:
            'What are your current support activities to deliver value to your customers?',
          options: [
            'Develop Vision and Strategy',
            'Develop and Manage Human Capital',
            'Manage Information Technology (IT)',
            'Manage Financial Resources',
            'Manage Enterprise Risk, Compliance, Remediation, and Resiliency',
            'Manage External Relationships',
            'Develop and Manage Business Capabilities',
            'Other (please specify)',
          ],
        },
        {
          id: 31,
          section: 'Improving Value Chain Activities to Better Serve Customers',
          question:
            'For which value chain activities do you think your company has the highest potential to leverage more technology to deliver better products and services?',
          followup: ['Choose up to 3 activities.'],
        },
        {
          id: 32,
          section: 'Improving Value Chain Activities to Better Serve Customers',
          question: 'Please share the thinking on the previous response.',
        },
        {
          id: 33,
          section: 'Improving Value Chain Activities for Financial Improvement',
          question:
            "After reviewing your company's operating expense analysis, are there any surprising or unexpected learnings?",
        },
        {
          id: 34,
          section: 'Improving Value Chain Activities for Financial Improvement',
          question:
            "After reviewing your company's capital expense analysis, are there any surprising or unexpected learnings?",
        },
        {
          id: 35,
          section: 'Improving Value Chain Activities for Financial Improvement',
          question:
            'For which value chain activities do you think your company has the highest potential to leverage more technology to realize a financial benefit?',
          followup: ['Choose up to 3 activities.'],
        },
        {
          id: 36,
          section: 'Improving Value Chain Activities for Financial Improvement',
          question: 'Please share the thinking on the previous response.',
        },
        {
          id: 37,
          section: 'Improving Value Chain Activities for Financial Improvement',
          question:
            'What are immediate opportunities, if any, to spend more efficiently?',
        },
        {
          id: 38,
          section: 'Improving Value Chain Activities for Competitive Differentiation',
          question:
            'Which activity is the best candidate for increasing product and/or service differentiation from the company\'s competition?',
        },
        {
          id: 39,
          section: 'Improving Value Chain Activities for Competitive Differentiation',
          question: 'Please share the thinking on the previous response.',
        },
        {
          id: 40,
          section: 'Improving Value Chain Activities for Competitive Differentiation',
          question: 'What would be required to make the improvement?',
        },
      ],
      prework: [
        {
          id: 1,
          question:
            'During the onboarding process, you provided the top five industries your company targets. If there are any edits or updates you would like to make, please do so here.',
          attribute: 'top5Industries',
          optionsConstant: 'industriesServed',
          exactCount: 5,
        },
        {
          id: 2,
          question:
            "Revenue analysis: Upload a spreadsheet segmenting your last year's revenue by product and service, including percentage of total revenue.",
          followup: [
            '(If you prefer, the analyses for 2, 3, and 4 can be separate tabs in the same workbook. In that case, load the file as a response to 2, and mark the other questions as completed.)',
          ],
          fileAttribute: 'revenueAnalysisByProductService',
          fileAcceptedTypes: ['.xlsx', '.xls', '.csv'],
        },
        {
          id: 3,
          question:
            "Revenue analysis: Upload a spreadsheet segmenting your last year's revenue revenue by industry vertical, including percentage of total revenue.",
          fileAttribute: 'revenueAnalysisByCustomerIndustry',
          markCompleted: true,
          fileAcceptedTypes: ['.xlsx', '.xls', '.csv'],
        },
        {
          id: 4,
          question:
            "Revenue analysis: Upload a spreadsheet segmenting your last year's revenue by customer, including percentage of total revenue.",
          fileAttribute: 'revenueAnalysisByCustomer',
          markCompleted: true,
          fileAcceptedTypes: ['.xlsx', '.xls', '.csv'],
        },
        {
          id: 5,
          question:
            "During the onboarding process, you provided a description of your company's products and services. If there are any edits or updates you would like to make, please do so here.",
          attribute: 'description',
        },
        {
          id: 6,
          question:
            'Expense analysis: Upload a spreadsheet listing your key operating expenses (OpEx) from last year in dollar amounts, including percentage of total OpEx.',
          fileAttribute: 'operatingExpenseAnalysis',
          fileAcceptedTypes: ['.xlsx', '.xls', '.csv'],
        },
        {
          id: 7,
          question:
            'Expense analysis: Upload a spreadsheet listing your key capital expenses (CapEx) from last year in dollar amounts, including percentage of total CapEx.',
          fileAttribute: 'capitalExpenseAnalysis',
          fileAcceptedTypes: ['.xlsx', '.xls', '.csv'],
        },
      ],
    },
    {
      index: 2,
      sessionNumber: 2,
      title: 'Growth Opportunities and Action Planning',
      description:
        "In this session, you'll identify and prioritize growth opportunities, then create an actionable plan to capture them. You'll explore new markets, develop your big idea for scaling to $100M, and establish the infrastructure and financing needed to execute your growth strategy.",
      duration: 4,
      questions: [
        {
          id: 1,
          section: 'Industry Revenue Opportunities and Threats',
          question:
            "Using information from the Trends and Opportunities report and from your team's own research, identify three trends leading to significant revenue growth within your industry?",
        },
        {
          id: 2,
          section: 'Industry Revenue Opportunities and Threats',
          question:
            "What implications do these trends have for your company's opportunities to capture additional value?",
        },
        {
          id: 3,
          section: 'Industry Revenue Opportunities and Threats',
          question:
            "What are three trends significantly threatening/impeding revenue growth within your company's industry?",
        },
        {
          id: 4,
          section: 'Industry Revenue Opportunities and Threats',
          question:
            "What implications do these trends have for your company's opportunities to capture additional value?",
        },
        {
          id: 5,
          section: 'New Customers, New Markets',
          question:
            "Based on your company's understanding, what problems could your company solve for new customers that it is already solving for current customers?",
        },
        {
          id: 6,
          section: 'New Customers, New Markets',
          question:
            'What is the price range (per unit of products or services) of the product or service that your company would provide to a new customer over one year?',
        },
        {
          id: 7,
          section: 'New Customers, New Markets',
          question:
            'What is the quantity range of products or services that you would project to provide to a new customer in one year?',
        },
        {
          id: 8,
          section: 'New Customers, New Markets',
          question:
            'How frequently would the new customer buy this quantity of products or services from your company?',
        },
        {
          id: 9,
          section: 'New Customers, New Markets',
          question:
            'How many new customers could you add at that quantity level right now?',
        },
        {
          id: 10,
          section: 'New Customers, New Markets',
          question:
            'Using the responses to questions, 6-9, what is the estimated value of the opportunity (in dollars) to serve these new customers?',
        },
        {
          id: 11,
          section: 'New Customers, New Markets',
          question: 'How could your company reach these new customers?',
        },
        {
          id: 12,
          section: 'New Customers, New Markets',
          question:
            'How will your product or service offerings need to change, if at all, to serve the new customers?',
        },
        {
          id: 13,
          section: 'New Customers, New Markets',
          question:
            'What opportunities does your company have for premium pricing of these products or services?',
        },
        {
          id: 14,
          section: 'New Customers, New Markets',
          question:
            'How would OpEx and/or CapEx spending need to change, if at all, to capture the identified opportunity with the new customers?',
        },
        {
          id: 15,
          section: 'New Products and Services',
          question:
            'Based on your understanding of today, what is one new problem your company could solve for current and new customers (using an existing or new product or service)?',
        },
        {
          id: 16,
          section: 'New Products and Services',
          question:
            'What would be required to solve this new problem (e.g., developing new products and services, forming strategic partnerships to provide new solutions, etc.)?',
        },
        {
          id: 17,
          section: 'New Products and Services',
          question:
            'Please estimate the price range (per unit of the product or service) that your company would charge for the product or service to solve this problem for a customer over one year?',
        },
        {
          id: 18,
          section: 'New Products and Services',
          question:
            'What is the quantity range of the product or service that you would project to provide the customer in one year?',
        },
        {
          id: 19,
          section: 'New Products and Services',
          question:
            'How frequently would a customer buy this quantity of products or services from your company?',
        },
        {
          id: 20,
          section: 'New Products and Services',
          question:
            'For how many customers could you provide this new product or service right now?',
        },
        {
          id: 21,
          section: 'New Products and Services',
          question:
            'Using the responses to questions 17-20, what is the estimated value of the opportunity (in dollars) to solve this new problem for customers?',
        },
        {
          id: 22,
          section: 'New Products and Services',
          question:
            'Revenue Model: For the new product or service, which revenue model(s) would allow you to capture the identified opportunities and work best for your customers?',
          options: [
            'Subscription',
            'Markup/Cost plus',
            'Licensing',
            'Advertising',
            'Affiliate Commission',
            'Data Sales',
            'Project-Based Services',
            'Retainer-Based Services',
            'Tickets, Events, Workshops',
            'Royalties',
            'Rent/Lease',
          ],
        },
        {
          id: 23,
          section: 'New Products and Services',
          question:
            'How would OpEx and/or CapEx spending need to change, if at all, to capture the opportunity of providing the new solution to customers?',
        },
        {
          id: 24,
          section: 'The Big Idea',
          question:
            'Based on your understanding of today and your visualization of tomorrow (from all of the work in the SGP modules and outside of Scaleup), articulate a big idea that will get your company from where it is today to $100M in annual revenue.',
        },
        {
          id: 25,
          section: 'Engaging the Customer',
          question:
            'To capture the value identified in the previous session, how does your company need to change its perception in the marketplace, if at all?',
        },
        {
          id: 26,
          section: 'Engaging the Customer',
          question:
            'To capture the identified value, what experience do customers need to feel when they use your product or service?',
        },
        {
          id: 27,
          section: 'Engaging the Customer',
          question:
            'What experience(s) do you want to avoid giving customers when they use your product or service?',
        },
        {
          id: 28,
          section: 'Go to Market: Getting to the Customer',
          question:
            'Name specific new industries you plan for your company to penetrate.',
        },
        {
          id: 29,
          section: 'Go to Market: Getting to the Customer',
          question: 'Name specific new customers you plan for your company to target.',
        },
        {
          id: 30,
          section: 'Scaling the Infrastructure',
          question:
            'Organization: What areas of functional and/or industry expertise does your company need to build to capture the identified value?',
        },
        {
          id: 31,
          section: 'Scaling the Infrastructure',
          question:
            'Organization: What challenges do you anticipate in growing your team and talent?',
        },
        {
          id: 32,
          section: 'Scaling the Infrastructure',
          question:
            'What types of technology would be most helpful to your company as it scales (i.e. CRM, ERP, financial/accounting, etc.)?',
        },
        {
          id: 33,
          section: 'Scaling the Infrastructure',
          question:
            'What kinds of business support (i.e. recruiting, marketing, legal, technology, etc.) do you think your company will need as it scales?',
        },
        {
          id: 34,
          section: 'Financing the Growth',
          question:
            "Review the company's last three years financial statements (income statement, balance sheet, and statement of cash flows). What tells you that the company is in a good (or not so good) position to embark on the Big Idea?",
        },
        {
          id: 35,
          section: 'Financing the Growth',
          question:
            "Reflecting on the company's financial position and what's needed to deliver on the Big Idea, develop a list of very high level estimates of the major financial impacts that will be experienced as the company delivers on the Big Idea.",
        },
        {
          id: 36,
          section: 'Financing the Growth',
          question:
            'How much capital does the company need to take the desired action steps over the next 12 months?',
        },
        {
          id: 37,
          section: 'Financing the Growth',
          question:
            'Does the company have the capital it needs to take the desired action steps within the next 12 months?',
        },
        {
          id: 38,
          section: 'Financing the Growth',
          question:
            'If the company will need capital over the next twelve months, what types of capital will be considered?',
        },
        {
          id: 39,
          section: 'Financing the Growth',
          question:
            'If the company will need additional capital to reach the $100M+ revenue goal, what types of capital will be considered?',
          options: [
            'Bank term loan',
            'Convertible debt',
            'Factoring',
            'Friends and family',
            'Line of credit',
            'Other loans',
            'Private credit',
            'Private equity',
            'Venture capital',
          ],
        },
      ],
      prework: [
        {
          id: 1,
          question:
            "Financial Statement analysis: Upload a file including the company's last three years financial statements (income statement, balance sheet, and statement of cash flows). The best files are typically PDF files, Microsoft Office documents (Excel, Word, or Powerpoint), or Google Documents (Sheets, Docs, or Slides).",
          fileAttribute: 'financialStatements',
          fileAcceptedTypes: [
            '.pdf',
            '.doc',
            '.docx',
            '.xls',
            '.xlsx',
            '.ppt',
            '.pptx',
          ],
        },
      ],
    },
  ],
  '5': [
    {
      index: 1,
      sessionNumber: 1,
      title: 'Profiling the supplier and their customers',
      description:
        'Gain a deeper understanding of your business and the customers you serve. In this session, you\'ll define your ideal customer profile, identify key needs and challenges, and assess how your products or services provide value. This foundation will help you refine your positioning and align with market demand.',
      duration: 2,
      questions: [
        {
          id: 1,
          section: 'Reviewing the Current State',
          question:
            'To ensure your team has a common understanding of your current market focus, what are the top five industries your company targets?',
          followup: ['Are the final five surprising?'],
        },
        {
          id: 2,
          section: 'Reviewing the Current State',
          question:
            "After reviewing your company's revenue analysis, are there any surprising or unexpected learnings?",
        },
        {
          id: 3,
          section: 'Reviewing the Current State',
          question:
            "Does the description of your company's products and services align with how your customer would articulate what your company provides?",
          followup: [
            'If not, how would your customers define your products and services?',
          ],
        },
        {
          id: 4,
          section: 'Reviewing the Current State',
          question:
            'To understand the main driver of your revenue, what do your customers like most about your products and services?',
        },
        {
          id: 5,
          section: 'Profiling Your Customer',
          question:
            'To create a detailed understanding of who your customers are, how would you define the demographic profile of the decision maker for your customer?',
        },
        {
          id: 6,
          section: 'Profiling Your Customer',
          question:
            'To understand how your customers think, including their values, attitudes, and lifestyle, how would you define the psychographic profile of the decision maker for your customer?',
        },
        {
          id: 7,
          section: 'Profiling Your Customer',
          question:
            'To analyze how your customers act, including their purchasing behavior and interaction with your products, how would you define the behavioral profile of the typical decision maker for your customer?',
        },
        {
          id: 8,
          section: 'Profiling Your Customer',
          question:
            'To map out where your customers are located and understand their work environments, what is the geographic profile of the typical decision maker for your customer?',
        },
        {
          id: 9,
          section: 'Profiling Your Customer',
          question:
            'If the decision maker is different from the user, what is the profile of the target user of your product or service?',
          followup: ['In their job, what do they care about most?'],
        },
        {
          id: 10,
          section: 'Profiling Your Customer',
          question:
            'To clearly define why your customer comes to you, what are the top three problems that you solve for your customers?',
        },
        {
          id: 11,
          section: 'Profiling Your Customer',
          question:
            'To identify unmet needs and potential new opportunities for product development or service enhancement, what have your customers requested from you that fall outside of your current offerings?',
        },
        {
          id: 12,
          section: 'Sales Approach and Scaleup Support',
          question:
            "What are your organization's strengths and areas of improvement in the enterprise sales function?",
        },
        {
          id: 13,
          section: 'Sales Approach and Scaleup Support',
          question:
            'What technology (ies) (e.g. tools and software) does your company use to aid in the sales process, both internally and externally?',
        },
        {
          id: 14,
          section: 'Sales Approach and Scaleup Support',
          question:
            "Excluding those with whom you already do business, which of Scaleup's current enterprise customers fit your target customer persona?",
        },
        {
          id: 15,
          section: 'Sales Approach and Scaleup Support',
          question:
            'If Scaleup could support your sales process in pursuit of three Fortune 500 corporations, which three would you choose?',
        },
        {
          id: 16,
          section: 'Sales Approach and Scaleup Support',
          question: 'How could Scaleup be most helpful to you in the sales process?',
        },
        {
          id: 17,
          section: 'Sales Approach and Scaleup Support',
          question: 'What do you not want Scaleup to do in this process?',
        },
        {
          id: 18,
          section: 'Projecting Future State',
          question:
            'How should the description of your products and services change over the next seven years (by 2031)?',
        },
        {
          id: 19,
          section: 'Projecting Future State',
          question:
            'How should the top five industries your company targets change in the next seven years (by 2031)?',
        },
        {
          id: 20,
          section: 'Projecting Future State',
          question:
            'How should the revenue analysis change in the next seven years (by 2031)?',
        },
      ],
      prework: [
        {
          id: 1,
          question:
            'During the onboarding process, you provided the top five industries your company targets. If there are any edits or updates you would like to make, please do so here.',
          attribute: 'top5Industries',
          optionsConstant: 'industriesServed',
          exactCount: 5,
        },
        {
          id: 2,
          question:
            "Revenue analysis: Upload a spreadsheet segmenting your last year's revenue by product and service, including percentage of total revenue.",
          followup: [
            '(If you prefer, the analyses for 2, 3, and 4 can be separate tabs in the same workbook. In that case, load the file as a response to 2, and mark the other questions as completed.)',
          ],
          fileAttribute: 'revenueAnalysisByProductService',
          fileAcceptedTypes: ['.xlsx', '.xls', '.csv'],
        },
        {
          id: 3,
          question:
            "Revenue analysis: Upload a spreadsheet segmenting your last year's revenue revenue by industry vertical, including percentage of total revenue.",
          fileAttribute: 'revenueAnalysisByCustomerIndustry',
          markCompleted: true,
          fileAcceptedTypes: ['.xlsx', '.xls', '.csv'],
        },
        {
          id: 4,
          question:
            "Revenue analysis: Upload a spreadsheet segmenting your last year's revenue by customer, including percentage of total revenue.",
          fileAttribute: 'revenueAnalysisByCustomer',
          markCompleted: true,
          fileAcceptedTypes: ['.xlsx', '.xls', '.csv'],
        },
        {
          id: 5,
          question:
            "During the onboarding process, you provided a description of your company's products and services. If there are any edits or updates you would like to make, please do so here.",
          attribute: 'description',
        },
      ],
    },
    {
      index: 2,
      sessionNumber: 2,
      title: "Understanding the supplier's strategic position",
      description:
        'Analyze where your business stands in the market and how it compares to competitors. This session will guide you through evaluating your company\'s strengths, weaknesses, opportunities, and external risks. You\'ll leave with a clearer picture of your strategic positioning and how to build a competitive advantage.',
      duration: 2,
      questions: [
        {
          id: 1,
          section: 'Identifying the Competitors',
          question: "Who are your company's top three to five direct competitors?",
        },
        {
          id: 2,
          section: 'Identifying the Competitors',
          question:
            "If the types of products and services (provided by your company and its direct competitors) didn't exist, what alternatives or substitutes do your customers have to get their needs met?",
        },
        {
          id: 3,
          section: 'Understanding Competitive Positioning',
          question:
            'How does your company differentiate its products and services from the competition?',
        },
        {
          id: 4,
          section: 'Understanding Competitive Positioning',
          question:
            'If your customer were asked, "Why do you choose your company\'s products and/or services?", how would they respond?',
        },
        {
          id: 5,
          section: 'Understanding Competitive Positioning',
          question:
            'If your customer were asked, "Why do you choose {question 6 alternative and substitute products and/or services ?", how would they respond?',
        },
        {
          id: 6,
          section: 'Replicating Great Customer Experiences',
          question:
            'Thinking about customer experience (even in B2B), how do your customers feel when they use your product or service?',
          followup: [
            'e.g., empowered, accomplished, heard, discouraged, confused, upset, happy, etc.',
          ],
        },
        {
          id: 7,
          section: 'Replicating Great Customer Experiences',
          question:
            'Describe three of your best customer success stories, including background, action, and result.',
        },
        {
          id: 8,
          section: 'Replicating Great Customer Experiences',
          question:
            'Which of those success stories, if any, do you believe you should try to replicate and why?',
        },
      ],
    },
    {
      index: 3,
      sessionNumber: 3,
      title: 'Dissecting the value chain',
      description:
        'Break down the core activities that drive your business success. This session will help you map out your value chain—from sourcing and production to distribution and customer service. Identifying inefficiencies and opportunities for optimization will set the stage for scalable growth.',
      duration: 2,
      questions: [
        {
          id: 1,
          section: 'Identifying Value Chain Activities',
          question:
            'What are your core activities to deliver value to your customers?',
          options: [
            'Develop and Manage Products and Services',
            'Market and Sell Products and Services',
            'Manage Supply Chain for Physical Products',
            'Deliver Services',
            'Manage Customer Service',
            'Acquire, Construct, and Manage Assets',
            'Other (please specify)',
          ],
        },
        {
          id: 2,
          section: 'Identifying Value Chain Activities',
          question:
            'What are your current support activities to deliver value to your customers?',
          options: [
            'Develop Vision and Strategy',
            'Develop and Manage Human Capital',
            'Manage Information Technology (IT)',
            'Manage Financial Resources',
            'Manage Enterprise Risk, Compliance, Remediation, and Resiliency',
            'Manage External Relationships',
            'Develop and Manage Business Capabilities',
            'Other (please specify)',
          ],
        },
        {
          id: 3,
          section: 'Improving Value Chain Activities to Better Serve Customers',
          question:
            'For which value chain activities do you think your company has the highest potential to leverage more technology to deliver better products and services?',
          followup: ['Choose up to 3 activities.'],
        },
        {
          id: 4,
          section: 'Improving Value Chain Activities to Better Serve Customers',
          question: 'Please share the thinking on the previous response.',
        },
        {
          id: 5,
          section: 'Improving Value Chain Activities for Financial Improvement',
          question:
            "After reviewing your company's operating expense analysis, are there any surprising or unexpected learnings?",
        },
        {
          id: 6,
          section: 'Improving Value Chain Activities for Financial Improvement',
          question:
            "After reviewing your company's capital expense analysis, are there any surprising or unexpected learnings?",
        },
        {
          id: 7,
          section: 'Improving Value Chain Activities for Financial Improvement',
          question:
            'For which value chain activities do you think your company has the highest potential to leverage more technology to realize a financial benefit?',
          followup: ['Choose up to 3 activities.'],
        },
        {
          id: 8,
          section: 'Improving Value Chain Activities for Financial Improvement',
          question: 'Please share the thinking on the previous response.',
        },
        {
          id: 9,
          section: 'Improving Value Chain Activities for Financial Improvement',
          question:
            'What are immediate opportunities, if any, to spend more efficiently?',
        },
        {
          id: 10,
          section: 'Improving Value Chain Activities for Competitive Differentiation',
          question:
            'Which activity is the best candidate for increasing product and/or service differentiation from the company\'s competition?',
        },
        {
          id: 11,
          section: 'Improving Value Chain Activities for Competitive Differentiation',
          question: 'Please share the thinking on the previous response.',
        },
        {
          id: 12,
          section: 'Improving Value Chain Activities for Competitive Differentiation',
          question: 'What would be required to make the improvement?',
        },
      ],
      prework: [
        {
          id: 1,
          question:
            'Expense analysis: Upload a spreadsheet listing your key operating expenses (OpEx) from last year in dollar amounts, including percentage of total OpEx.',
          fileAttribute: 'operatingExpenseAnalysis',
          fileAcceptedTypes: ['.xlsx', '.xls', '.csv'],
        },
        {
          id: 2,
          question:
            'Expense analysis: Upload a spreadsheet listing your key capital expenses (CapEx) from last year in dollar amounts, including percentage of total CapEx.',
          fileAttribute: 'capitalExpenseAnalysis',
          fileAcceptedTypes: ['.xlsx', '.xls', '.csv'],
        },
      ],
    },
    {
      index: 4,
      sessionNumber: 4,
      title: 'Identifying and prioritizing growth opportunities',
      description:
        'Explore new paths for expansion and revenue growth. In this session, you\'ll brainstorm potential growth strategies, evaluate their feasibility, and prioritize the most impactful opportunities. This discussion will help you focus your efforts on the areas that drive sustainable business success.',
      duration: 3,
      questions: [
        {
          id: 1,
          section: 'Industry Revenue Opportunities and Threats',
          question:
            "Using information from the Trends and Opportunities report and from your team's own research, identify three trends leading to significant revenue growth within your industry?",
        },
        {
          id: 2,
          section: 'Industry Revenue Opportunities and Threats',
          question:
            "What implications do these trends have for your company's opportunities to capture additional value?",
        },
        {
          id: 3,
          section: 'Industry Revenue Opportunities and Threats',
          question:
            "What are three trends significantly threatening/impeding revenue growth within your company's industry?",
        },
        {
          id: 4,
          section: 'Industry Revenue Opportunities and Threats',
          question:
            "What implications do these trends have for your company's opportunities to capture additional value?",
        },
        {
          id: 5,
          section: 'New Customers, New Markets',
          question:
            "Based on your company's understanding, what problems could your company solve for new customers that it is already solving for current customers?",
        },
        {
          id: 6,
          section: 'New Customers, New Markets',
          question:
            'What is the price range (per unit of products or services) of the product or service that your company would provide to a new customer over one year?',
        },
        {
          id: 7,
          section: 'New Customers, New Markets',
          question:
            'What is the quantity range of products or services that you would project to provide to a new customer in one year?',
        },
        {
          id: 8,
          section: 'New Customers, New Markets',
          question:
            'How frequently would the new customer buy this quantity of products or services from your company?',
        },
        {
          id: 9,
          section: 'New Customers, New Markets',
          question:
            'How many new customers could you add at that quantity level right now?',
        },
        {
          id: 10,
          section: 'New Customers, New Markets',
          question:
            'Using the responses to questions, 6-9, what is the estimated value of the opportunity (in dollars) to serve these new customers?',
        },
        {
          id: 11,
          section: 'New Customers, New Markets',
          question: 'How could your company reach these new customers?',
        },
        {
          id: 12,
          section: 'New Customers, New Markets',
          question:
            'How will your product or service offerings need to change, if at all, to serve the new customers?',
        },
        {
          id: 13,
          section: 'New Customers, New Markets',
          question:
            'What opportunities does your company have for premium pricing of these products or services?',
        },
        {
          id: 14,
          section: 'New Customers, New Markets',
          question:
            'How would OpEx and/or CapEx spending need to change, if at all, to capture the identified opportunity with the new customers?',
        },
        {
          id: 15,
          section: 'New Products and Services',
          question:
            'Based on your understanding of today, what is one new problem your company could solve for current and new customers (using an existing or new product or service)?',
        },
        {
          id: 16,
          section: 'New Products and Services',
          question:
            'What would be required to solve this new problem (e.g., developing new products and services, forming strategic partnerships to provide new solutions, etc.)?',
        },
        {
          id: 17,
          section: 'New Products and Services',
          question:
            'Please estimate the price range (per unit of the product or service) that your company would charge for the product or service to solve this problem for a customer over one year?',
        },
        {
          id: 18,
          section: 'New Products and Services',
          question:
            'What is the quantity range of the product or service that you would project to provide the customer in one year?',
        },
        {
          id: 19,
          section: 'New Products and Services',
          question:
            'How frequently would a customer buy this quantity of products or services from your company?',
        },
        {
          id: 20,
          section: 'New Products and Services',
          question:
            'For how many customers could you provide this new product or service right now?',
        },
        {
          id: 21,
          section: 'New Products and Services',
          question:
            'Using the responses to questions 17-20, what is the estimated value of the opportunity (in dollars) to solve this new problem for customers?',
        },
        {
          id: 22,
          section: 'New Products and Services',
          question:
            'Revenue Model: For the new product or service, which revenue model(s) would allow you to capture the identified opportunities and work best for your customers?',
          options: [
            'Subscription',
            'Markup/Cost plus',
            'Licensing',
            'Advertising',
            'Affiliate Commission',
            'Data Sales',
            'Project-Based Services',
            'Retainer-Based Services',
            'Tickets, Events, Workshops',
            'Royalties',
            'Rent/Lease',
          ],
        },
        {
          id: 23,
          section: 'New Products and Services',
          question:
            'How would OpEx and/or CapEx spending need to change, if at all, to capture the opportunity of providing the new solution to customers?',
        },
        {
          id: 24,
          section: 'The Big Idea',
          question:
            'Based on your understanding of today and your visualization of tomorrow (from all of the work in the SGP modules and outside of Scaleup), articulate a big idea that will get your company from where it is today to $100M in annual revenue.',
        },
      ],
    },
    {
      index: 5,
      sessionNumber: 5,
      title: 'Action planning to capture growth opportunities',
      description:
        'Turn strategy into action with a structured implementation plan. This final session will guide you through defining key milestones, assigning responsibilities, and establishing measurable success metrics. By the end, you\'ll have a clear roadmap to execute your growth strategy effectively.',
      duration: 3,
      questions: [
        {
          id: 1,
          section: 'Engaging the Customer',
          question:
            'To capture the value identified in Strategy Session 4, how does your company need to change its perception in the marketplace, if at all?',
        },
        {
          id: 2,
          section: 'Engaging the Customer',
          question:
            'To capture the value identified in Strategy Session 4, what experience do customers need to feel when they use your product or service?',
        },
        {
          id: 3,
          section: 'Engaging the Customer',
          question:
            'What experience(s) do you want to avoid giving customers when they use your product or service?',
        },
        {
          id: 4,
          section: 'Go to Market: Getting to the Customer',
          question:
            'Name specific new industries you plan for your company to penetrate.',
        },
        {
          id: 5,
          section: 'Go to Market: Getting to the Customer',
          question: 'Name specific new customers you plan for your company to target.',
        },
        {
          id: 6,
          section: 'Scaling the Infrastructure',
          question:
            'Organization: What areas of functional and/or industry expertise does your company need to build to capture the identified value?',
        },
        {
          id: 7,
          section: 'Scaling the Infrastructure',
          question:
            'Organization: What challenges do you anticipate in growing your team and talent?',
        },
        {
          id: 8,
          section: 'Scaling the Infrastructure',
          question:
            'What types of technology would be most helpful to your company as it scales (i.e. CRM, ERP, financial/accounting, etc.)?',
        },
        {
          id: 9,
          section: 'Scaling the Infrastructure',
          question:
            'What kinds of business support (i.e. recruiting, marketing, legal, technology, etc.) do you think your company will need as it scales?',
        },
        {
          id: 10,
          section: 'Financing the Growth',
          question:
            "Review the company's last three years financial statements (income statement, balance sheet, and statement of cash flows). What tells you that the company is in a good (or not so good) position to embark on the Big Idea?",
        },
        {
          id: 11,
          section: 'Financing the Growth',
          question:
            "Reflecting on the company's financial position and what's needed to deliver on the Big Idea from Strategy Session 4, develop a list of very high level estimates of the major financial impacts that will be experienced as the company delivers on the Big Idea.",
        },
        {
          id: 12,
          section: 'Financing the Growth',
          question:
            'How much capital does the company need to take the desired action steps over the next 12 months?',
        },
        {
          id: 13,
          section: 'Financing the Growth',
          question:
            'Does the company have the capital it needs to take the desired action steps within the next 12 months?',
        },
        {
          id: 14,
          section: 'Financing the Growth',
          question:
            'If the company will need capital over the next twelve months, what types of capital will be considered?',
        },
        {
          id: 15,
          section: 'Financing the Growth',
          question:
            'If the company will need additional capital to reach the $100M+ revenue goal, what types of capital will be considered?',
          options: [
            'Bank term loan',
            'Convertible debt',
            'Factoring',
            'Friends and family',
            'Line of credit',
            'Other loans',
            'Private credit',
            'Private equity',
            'Venture capital',
          ],
        },
      ],
      prework: [
        {
          id: 1,
          question:
            "Financial Statement analysis: Upload a file including the company's last three years financial statements (income statement, balance sheet, and statement of cash flows). The best files are typically PDF files, Microsoft Office documents (Excel, Word, or Powerpoint), or Google Documents (Sheets, Docs, or Slides).",
          fileAttribute: 'financialStatements',
          fileAcceptedTypes: [
            '.pdf',
            '.doc',
            '.docx',
            '.xls',
            '.xlsx',
            '.ppt',
            '.pptx',
          ],
        },
      ],
    },
  ],
};
